<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CompanyPresenterV2 from '@satellite/components/elements/presenters/CompanyPresenterV2.vue';
import PresenterValueFallback from '@satellite/components/elements/presenters/PresenterValueFallback.vue';
import YardAssetVisitStateIcon from '@satellite/components/elements/presenters/yard/YardAssetVisitStateIcon.vue';
import YardSpotPresenterV2 from '@satellite/components/elements/presenters/YardSpotPresenterV2.vue';

import type {
  YardAssetVisitDisplayField,
  YardAssetVisitDisplayData
} from '@satellite/composables/useYardAssetVisitDisplay';

const {
  data,
  orientation = 'horizontal',
  displayFields = ['visitType']
} = defineProps<{
  data: YardAssetVisitDisplayData;
  orientation?: 'horizontal' | 'vertical';
  displayFields?: YardAssetVisitDisplayField[];
}>();

const { t } = useI18n();

const showVisitType = computed(() => displayFields.includes('visitType'));
const showVisitTypeChip = computed(() => displayFields.includes('visitTypeChip'));
const showPhone = computed(() => displayFields.includes('phone'));
const showCompanyName = computed(() => displayFields.includes('companyName'));
const showCompanyScac = computed(() => displayFields.includes('companyScac'));
const showLicensePlate = computed(() => displayFields.includes('licensePlate'));
const showDotNumber = computed(() => displayFields.includes('dotNumber'));
const showDriverNotes = computed(() => displayFields.includes('driverNotes'));
const showDriverAppointmentIdentifier = computed(() =>
  displayFields.includes('driverAppointmentIdentifier')
);
const showSpot = computed(() => displayFields.includes('spot'));
const showArrivedTime = computed(() => displayFields.includes('arrivedTime'));
const showDockedAt = computed(() => displayFields.includes('dockedAt'));
const showVisitTypeIcon = computed(() => displayFields.includes('visitTypeIcon'));
const showStateIcon = computed(() => displayFields.includes('stateIcon'));
const showTruckIcon = computed(() => displayFields.includes('truckIcon'));
</script>

<template>
  <span
    class="d-flex yard-asset-visit-display-v2"
    v-bind="$attrs"
    :class="{
      'flex-row align-center gap-4': orientation === 'horizontal',
      'flex-column align-start gap-2': orientation === 'vertical',
      'typography-body-sm': data.size === 'small',
      'typography-body-md': data.size === 'regular',
      'typography-heading-lg-bold': data.size === 'large'
    }">
    <template v-if="showVisitTypeIcon">
      <slot name="visit-type-icon" :visit-type-icon="data.visitTypeIcon">
        <v-icon v-bind="data.visitTypeIconAttrs" />
      </slot>
    </template>

    <template v-if="showStateIcon">
      <span class="d-flex flex-row gap-1 align-center">
        <yard-asset-visit-state-icon
          v-bind="data.stateIconAttrs"
          :asset-visit="data.visit"
          :loading="data.isFetching"
          :orientation="orientation"
          :warehouse-timezone="data.warehouseTimezone"
          show-container-count />
      </span>
    </template>

    <template v-if="showTruckIcon">
      <slot name="truck-icon" :truck-icon="data.visitTypeIcon">
        <v-icon v-bind="data.truckIconAttrs" />
      </slot>
    </template>

    <template v-if="showCompanyName">
      <template v-if="data.companyName">
        <slot
          name="company-name"
          :company-name="data.companyName"
          :company-name-attrs="data.companyNameAttrs">
          <span v-bind="data.companyNameAttrs">
            {{ data.companyName }}
          </span>
        </slot>
      </template>
      <company-presenter-v2
        v-else-if="data.company?.id"
        :size="data.size"
        :company-id="data.company.id"
        :mode="data.mode"
        :display-fields="['name']"
        v-bind="data.companyPresenterProps">
        <template #company-name="{ companyName, companyNameAttrs }">
          <slot
            name="company-name"
            :company-name="companyName"
            :company-name-attrs="companyNameAttrs">
            <presenter-value-fallback
              v-if="!companyName"
              :text="t('core.yard.messages.unknownCompany')" />
            <span v-else v-bind="companyNameAttrs">
              {{ companyName }}
            </span>
          </slot>
        </template>
      </company-presenter-v2>
      <presenter-value-fallback v-else :text="t('core.yard.messages.unhooked')" />
    </template>

    <template v-if="showCompanyScac">
      <template v-if="data.companyScac">
        <slot
          name="company-scac"
          :company-scac="data.companyScac"
          :company-scac-attrs="data.companyScacAttrs">
          <span v-bind="data.companyScacAttrs">
            {{ data.companyScac }}
          </span>
        </slot>
      </template>
      <company-presenter-v2
        v-else-if="data.company?.id"
        :size="data.size"
        :company-id="data.company.id"
        :mode="data.mode"
        :display-fields="['scac']"
        v-bind="data.companyPresenterProps">
        <template #company-scac="{ companyScac, companyScacAttrs }">
          <slot
            name="company-scac"
            :company-scac="companyScac"
            :company-scac-attrs="companyScacAttrs">
            <presenter-value-fallback v-if="!companyScac" />
            <span v-else v-bind="companyScacAttrs">
              {{ companyScac }}
            </span>
          </slot>
        </template>
      </company-presenter-v2>
      <presenter-value-fallback v-else />
    </template>

    <template v-if="showVisitType">
      <slot
        name="visit-type"
        :visit-type="data.visitTypeRaw"
        :visit-type-attrs="data.visitTypeAttrs">
        <presenter-value-fallback v-if="!data.visitTypeRaw" />
        <span v-else v-bind="data.visitTypeAttrs">
          {{ data.visitTypeLabel }}
          <template v-if="data.visitDirection">
            &bull;
            {{ data.visitDirection }}
          </template>
        </span>
      </slot>
    </template>

    <template v-if="showVisitTypeChip">
      <v-chip v-bind="data.visitTypeChipAttrs">
        <slot
          name="visit-type-chip"
          :visit-type="data.visitTypeLabel"
          :visit-direction="data.visitDirection"
          :visit-type-chip-text="data.visitTypeChipText"
          :visit-type-chip-text-attrs="data.visitTypeChipTextAttrs">
          <span v-bind="data.visitTypeChipTextAttrs">
            {{ data.visitTypeChipText }}
          </span>
        </slot>
      </v-chip>
    </template>

    <template v-if="showPhone">
      <slot name="phone" :phone="data.phone" :phone-attrs="data.phoneAttrs">
        <presenter-value-fallback v-if="!data.phone" />
        <span v-else v-bind="data.phoneAttrs">
          {{ data.phone }}
        </span>
      </slot>
    </template>

    <template v-if="showSpot">
      <span v-bind="data.spotAttrs">
        <template v-if="data.hasAssignment">
          <slot
            name="spot"
            :spot="data.spot"
            :spot-area="data.spotArea"
            :spot-id="data.spotId"
            :spot-presenter-props="data.spotPresenterProps">
            <yard-spot-presenter-v2
              :display-fields="['area', 'code']"
              :spot-id="data.spotId"
              :size="data.size"
              :mode="data.mode"
              v-bind="data.spotPresenterProps" />
          </slot>
        </template>

        <template v-else>
          <slot name="waiting-assignment" :has-departed="data.hasDeparted">
            <presenter-value-fallback data-testid="yard-asset-visit-presenter-waiting-assignment">
              <template #default>
                {{ t('core.yard.messages.waitingAssignment') }}
              </template>
            </presenter-value-fallback>
          </slot>
        </template>
      </span>
    </template>

    <template v-if="showDriverNotes">
      <slot
        name="driver-notes"
        :driver-notes="data.driverNotes"
        :driver-notes-attrs="data.driverNotesAttrs">
        <presenter-value-fallback v-if="!data.driverNotes" />
        <span v-else v-bind="data.driverNotesAttrs">
          {{ data.driverNotes }}
        </span>
      </slot>
    </template>

    <template v-if="showArrivedTime">
      <slot
        name="arrived-time"
        :arrived-time="data.arrivedTime"
        :arrived-time-formatted="data.arrivedTimeFormatted"
        :arrived-time-attrs="data.arrivedTimeAttrs">
        <presenter-value-fallback v-if="!data.arrivedTime" />
        <span v-else v-bind="data.arrivedTimeAttrs">
          {{ data.arrivedTimeFormatted }}
        </span>
      </slot>
    </template>

    <template v-if="showDockedAt">
      <slot
        name="docked-at"
        :docked-at="data.dockedAt"
        :docked-at-formatted="data.dockedAtFormatted"
        :docked-at-attrs="data.dockedAtAttrs">
        <presenter-value-fallback v-if="!data.dockedAt" />
        <span v-else v-bind="data.dockedAtAttrs">
          {{ data.dockedAtFormatted }}
        </span>
      </slot>
    </template>

    <template v-if="showDriverAppointmentIdentifier">
      <slot
        name="driver-appointment-identifier"
        :driver-appointment-identifier="data.driverAppointmentIdentifier"
        :driver-appointment-identifier-attrs="data.driverAppointmentIdentifierAttrs">
        <presenter-value-fallback v-if="!data.driverAppointmentIdentifier" />
        <span v-else v-bind="data.driverAppointmentIdentifierAttrs">
          {{ data.driverAppointmentIdentifier }}
        </span>
      </slot>
    </template>

    <template v-if="showLicensePlate">
      <slot
        name="license-plate"
        :license-plate="data.licensePlate"
        :license-plate-attrs="data.licensePlateAttrs">
        <presenter-value-fallback v-if="!data.licensePlate" />
        <span v-else v-bind="data.licensePlateAttrs">
          {{ data.licensePlate }}
        </span>
      </slot>
    </template>

    <template v-if="showDotNumber">
      <slot name="dot-number" :dot-number="data.dotNumber" :dot-number-attrs="data.dotNumberAttrs">
        <presenter-value-fallback v-if="!data.dotNumber" />
        <span v-else v-bind="data.dotNumberAttrs">
          {{ data.dotNumber }}
        </span>
      </slot>
    </template>

    <slot name="default" :visit="data.visit" />
  </span>
</template>
